import { Transition } from '@headlessui/react';
import { MedicalSample, MedicineBottle } from 'healthicons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Illustration from '#app/assets/images/glow-top.svg';
import Particles from './particles';

const options = [
	{
		value: 'features.option.receptionist',
		icon: <MedicineBottle className="h-5 w-5" />,
		selectedIcon: <MedicineBottle className="h-10 w-10" />,
	},
	{
		value: 'features.option.sampleReceptionist',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.validator',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.phlebotomist',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.labAssistant',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.branchAdmin',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.messenger',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.sampleAnalyst',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
	{
		value: 'features.option.labManager',
		icon: <MedicalSample className="h-5 w-5" />,
		selectedIcon: <MedicalSample className="h-10 w-10" />,
	},
];

export default function Features() {
	const [tab, setTab] = useState<number>(1);
	const { t } = useTranslation();

	return (
		<section>
			<div className="relative mx-auto max-w-6xl px-4 sm:px-6">
				{/* Illustration */}
				<div
					className="pointer-events-none absolute inset-0 -z-10 -mx-28 overflow-hidden rounded-t-[3rem]"
					aria-hidden="true"
				>
					<div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2">
						<img
							src={Illustration}
							className="max-w-none"
							width={1404}
							height={658}
							alt={t('features.illustrationAlt')}
						/>
					</div>
				</div>

				<div className="pb-12 pt-16 md:pb-20 md:pt-52">
					<div>
						{/* Section content */}
						<div className="mx-auto flex max-w-xl flex-col space-y-8 space-y-reverse md:max-w-none md:flex-row md:space-x-8 md:space-y-0 lg:space-x-16 xl:space-x-20">
							{/* Content */}
							<div
								className="order-1 max-md:text-center md:order-none md:w-7/12 lg:w-1/2"
								data-aos="fade-down"
							>
								<div>
									<div className="inline-flex bg-gradient-to-r from-teal-500 to-teal-200 bg-clip-text pb-3 font-medium text-transparent">
										{t('features.subheading')}
									</div>
								</div>
								<h3 className="h3 bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 bg-clip-text pb-3 text-transparent">
									{t('features.heading')}
								</h3>
								<p className="mb-8 text-lg text-slate-400">
									{t('features.description')}
								</p>
							</div>

							{/* Image */}
							<div
								className="md:absolute md:right-0 md:w-5/12 lg:w-1/2"
								data-aos="fade-up"
								data-aos-delay="100"
							>
								<div className="relative -mt-12 py-24">
									{/* Particles animation */}
									<Particles
										className="absolute inset-0 -z-10"
										quantity={8}
										staticity={30}
									/>

									<div className="flex items-center justify-center">
										<div className="relative flex h-48 w-48 items-center justify-center">
											{/* Halo effect */}
											<svg
												className="pointer-events-none absolute inset-0 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform blur-md will-change-transform"
												width="480"
												height="480"
												viewBox="0 0 480 480"
												xmlns="http://www.w3.org/2000/svg"
											>
												<defs>
													<linearGradient
														id="pulse-a"
														x1="50%"
														x2="50%"
														y1="100%"
														y2="0%"
													>
														<stop offset="0%" stopColor="#38B2AC" />
														<stop offset="76.382%" stopColor="#E6FFFA" />
														<stop offset="100%" stopColor="#319795" />
													</linearGradient>
												</defs>
												<g fillRule="evenodd">
													<path
														className="pulse"
														fill="url(#pulse-a)"
														fillRule="evenodd"
														d="M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z"
													/>
													<path
														className="pulse pulse-1"
														fill="url(#pulse-a)"
														fillRule="evenodd"
														d="M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z"
													/>
													<path
														className="pulse pulse-2"
														fill="url(#pulse-a)"
														fillRule="evenodd"
														d="M240,0 C372.5484,0 480,107.4516 480,240 C480,372.5484 372.5484,480 240,480 C107.4516,480 0,372.5484 0,240 C0,107.4516 107.4516,0 240,0 Z M240,88.8 C156.4944,88.8 88.8,156.4944 88.8,240 C88.8,323.5056 156.4944,391.2 240,391.2 C323.5056,391.2 391.2,323.5056 391.2,240 C391.2,156.4944 323.5056,88.8 240,88.8 Z"
													/>
												</g>
											</svg>
											{/* Grid */}
											<div className="pointer-events-none absolute inset-0 left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 transform overflow-hidden rounded-full [mask-image:_radial-gradient(black,_transparent_60%)]">
												<div className="h-[200%] animate-endless">
													<div className="absolute inset-0 opacity-20 blur-[2px] [background:_repeating-linear-gradient(transparent,_transparent_48px,_theme(colors.white)_48px,_theme(colors.white)_49px)]" />
													<div className="absolute inset-0 [background:_repeating-linear-gradient(transparent,_transparent_48px,_theme(colors.teal.500)_48px,_theme(colors.teal.500)_49px)]" />
													<div className="absolute inset-0 opacity-20 blur-[2px] [background:_repeating-linear-gradient(90deg,transparent,_transparent_48px,_theme(colors.white)_48px,_theme(colors.white)_49px)]" />
													<div className="absolute inset-0 [background:_repeating-linear-gradient(90deg,transparent,_transparent_48px,_theme(colors.teal.500)_48px,_theme(colors.teal.500)_49px)]" />
												</div>
											</div>
											{/* Icons */}
											{options.map((option, index) => (
												<Transition
													key={index}
													as="div"
													show={tab === index}
													className="absolute"
													enter="transition ease-[cubic-bezier(0.68,-0.3,0.32,1)] duration-700 order-first"
													enterFrom="opacity-0 -rotate-[60deg]"
													enterTo="opacity-100 rotate-0"
													leave="transition ease-[cubic-bezier(0.68,-0.3,0.32,1)] duration-700"
													leaveFrom="opacity-100 rotate-0"
													leaveTo="opacity-0 rotate-[60deg]"
												>
													<div className="relative flex h-16 w-16 -rotate-[14deg] items-center justify-center rounded-2xl border border-transparent shadow-2xl [background:linear-gradient(theme(colors.slate.900),_theme(colors.slate.900))_padding-box,_conic-gradient(theme(colors.slate.400),_theme(colors.slate.700)_25%,_theme(colors.slate.700)_75%,_theme(colors.slate.400)_100%)_border-box] before:absolute before:inset-0 before:rounded-2xl before:bg-slate-800/30">
														{option.selectedIcon}
													</div>
												</Transition>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="w-full">
						<div className="mt-8 grid w-full grid-cols-12 gap-4">
							{options.map((option, index) => (
								<button
									key={index}
									className={`col-span-12 flex w-full items-center gap-1 rounded border bg-slate-800/25 px-3 py-2 text-sm font-medium text-slate-50 transition duration-150 ease-in-out hover:opacity-100 md:col-span-6 lg:col-span-4 ${tab !== index ? 'border-slate-700 opacity-50' : 'border-teal-700 shadow shadow-teal-500/25'}`}
									onClick={() => setTab(index)}
								>
									{option.icon}
									<span>{t(option.value)}</span>
								</button>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
